import { mat4, vec3 } from 'gl-matrix';

// 将 css 中设置为 perspective 的元素通过渲染到 canvas 上
const VERTEX_SHADER_SOURCE = `
    attribute vec2 pos;
    attribute vec2 uv;

    varying vec2 vUv;

    uniform mat4 u_matrix;

    void main() {
        vUv = uv;
        vec4 position = u_matrix * vec4(pos, 0.0, 1.0);
        gl_Position = position;
    }
`;

const FRAGMENT_SHADER_SOURCE = `
    precision mediump float;

    uniform sampler2D src;
    varying vec2 vUv;

    void main() {
        gl_FragColor = texture2D(src, vUv);
    }
`;

function createShader(gl, type, source) {
    let shader = gl.createShader(type);
    if (!shader) {
        return;
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    let compiled = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
    if (!compiled) {
        let error = gl.getShaderInfoLog(shader);
        console.log(error);
        gl.deleteShader(shader);
        shader = null;
    }
    return shader;
}
function createProgram(gl, vShader, fShader) {
    let vertexShader = createShader(gl, gl.VERTEX_SHADER, vShader);
    let fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fShader);
    if (!vertexShader || !fragmentShader) {
        return;
    }
    let program = gl.createProgram();
    if (!program) {
        return;
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    let linked = gl.getProgramParameter(program, gl.LINK_STATUS);
    if (!linked) {
        let error = gl.getProgramInfoLog(program);
        gl.deleteProgram(program);
        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);
        program = null;
    }
    return program;
}
function initProgram(gl, vShader, fShader) {
    let program = createProgram(gl, vShader, fShader);
    if (!program) {
        return;
    }
    gl.program = program;
    return program;
}
function initVertexBuffer(gl) {
    const vertices = new Float32Array([-1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0, -1, 1, 0, 0, 1, -1, 1, 1, 1, 1, 1, 0]);
    let vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    let floatSize = Float32Array.BYTES_PER_ELEMENT;
    let vertSize = 4 * floatSize;
    let pos = gl.getAttribLocation(gl.program, 'pos');
    let uv = gl.getAttribLocation(gl.program, 'uv');
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, vertSize, 0);
    gl.enableVertexAttribArray(uv);
    gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, vertSize, 2 * floatSize);
}

function bindTexture(gl, texture) {
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}
function attachTexture(gl, texture, image) {
    bindTexture(gl, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
}

class FJCssPerspectiveToWebGLStore {
    _canvas;

    _gl = null;
    _program = null;
    _textureObject = null;
    _lastCanvasWidth = 0;
    _lastCanvasHeight = 0;

    constructor() {
        this._canvas = document.createElement('canvas');
    }

    /**
     * 检测 WebGL 上下文是否丢失
     * @returns {boolean} 如果上下文丢失返回 true，否则返回 false
     */
    isContextLost() {
        if (!this._gl && !this._program) {
            // 这里表示还没有初始化创建
            return false;
        }
        return this._gl.isContextLost();
    }

    /**
     * 销毁实例时清理事件监听器和资源
     */
    destroy() {
        // 清理 WebGL 资源
        if (this._textureObject && this._gl && !this._gl.isContextLost()) {
            this._gl.deleteTexture(this._textureObject);
        }

        this._textureObject = null;
        this._program = null;
        this._gl = null;
    }

    /**
     * 将 css 中设置为 perspective 的元素通过渲染到 canvas 上
     * @param {Object} options
     * @param {HTMLCanvasElement} options.canvas
     * @param {Array} options.transforms
     * @param {number} options.perspective
     * @param {boolean} options.rebindTexture 是否重新绑定纹理
     */
    convertCssPerspectiveToWebGL(options) {
        const { canvas, transforms, perspective, rebindTexture = false } = options;

        if (this.isContextLost()) {
            console.log('[WebGL]:WebGL context lost, cannot continue rendering');
            return;
        }

        if (!this._program) {
            this._initProgram({ width: canvas.width, height: canvas.height });
        }

        // 多元素、多尺寸输出性能瓶颈
        if (canvas.width !== this._canvas.width || canvas.height !== this._canvas.height) {
            this._canvas.width = canvas.width;
            this._canvas.height = canvas.height;
            this._gl.viewport(0, 0, canvas.width, canvas.height);
        }

        this._gl.enable(this._gl.DEPTH_TEST);
        // 清空画布;
        this._gl.clearColor(0, 0, 0, 0);
        this._gl.clear(this._gl.COLOR_BUFFER_BIT | this._gl.DEPTH_BUFFER_BIT);
        this._gl.useProgram(this._program);

        // 第一个性能瓶颈
        this._setUniforms(canvas, transforms, perspective, rebindTexture);

        // 多俩次的渲染  10 -> 14
        this._gl.drawArrays(this._gl.TRIANGLES, 0, 6);
        // Render to the canvas
        const context = canvas.getContext('2d');
        canvas.width = canvas.width;
        canvas.height = canvas.height;

        context.drawImage(this._canvas, 0, 0);
    }

    _setUniforms(canvas, transform, perspective, rebindTexture = false) {
        const isSameSize = canvas.width === this._lastCanvasWidth && canvas.height === this._lastCanvasHeight;

        if (!this._textureObject || !isSameSize) {
            this._gl.activeTexture(this._gl.TEXTURE0);
            if (this._textureObject) {
                this._gl.deleteTexture(this._textureObject); // Delete old texture if it exists
            }
            this._textureObject = this._gl.createTexture(); // Create a new texture
            attachTexture(this._gl, this._textureObject, canvas); // attachTexture binds, sets params, and uploads image

            this._lastCanvasWidth = canvas.width;
            this._lastCanvasHeight = canvas.height;
        }

        if (rebindTexture && this._textureObject && isSameSize) {
            this._gl.activeTexture(this._gl.TEXTURE0);
            this._gl.bindTexture(this._gl.TEXTURE_2D, this._textureObject);
            this._gl.texSubImage2D(this._gl.TEXTURE_2D, 0, 0, 0, this._gl.RGBA, this._gl.UNSIGNED_BYTE, canvas);
        }

        // 检查纹理对象是否有效
        if (!this._gl.isTexture(this._textureObject)) {
            console.log('[WebGL]:Invalid texture object, recreating texture');
        }

        let u_matrix = this._gl.getUniformLocation(this._program, 'u_matrix');
        let aspectRatio = canvas.width / canvas.height; // Aspect ratio of the canvas
        let fov = 2 * Math.atan(canvas.height / (2 * perspective)); // Field of modules. This formula converts the CSS perspective value to a field of modules angle in radians.
        let near = 0.001; // Near clipping plane
        let far = perspective; // Far clipping plane
        var f = Math.tan(Math.PI * 0.5 - 0.5 * fov);

        let cameraPosition = vec3.fromValues(0, 0, f);
        let targetPoint = vec3.fromValues(0, 0, 0);
        let upDirection = vec3.fromValues(0, 1, 0);

        // View matrix
        let viewMatrix = mat4.create();
        mat4.lookAt(viewMatrix, cameraPosition, targetPoint, upDirection);

        // Perspective projection matrix
        let projectionMatrix = mat4.create();
        mat4.perspective(projectionMatrix, fov, aspectRatio, near, far);

        // Combined modules-projection matrix
        let viewProjectionMatrix = mat4.create();
        mat4.multiply(viewProjectionMatrix, projectionMatrix, viewMatrix);

        let rotateX = 0;
        let rotateY = 0;
        let rotateZ = 0;
        let translateX = 0;
        let translateY = 0;
        let translateZ = 0;

        const __convertToValue = (str, isX) => {
            const distance = isX ? canvas.width : canvas.height;
            return str.indexOf('%') !== -1 ? (distance * parseFloat(str)) / 100 : parseFloat(str);
        };

        transform.forEach(item => {
            if (item.indexOf('rotateX') !== -1) {
                rotateX = -parseFloat(item.replace('rotateX(', '').replace('deg)', '')) * (Math.PI / 180);
                mat4.rotate(viewProjectionMatrix, viewProjectionMatrix, rotateX, [1, 0, 0]);
            } else if (item.indexOf('rotateY') !== -1) {
                rotateY = parseFloat(item.replace('rotateY(', '').replace('deg)', '')) * (Math.PI / 180);
                mat4.rotate(viewProjectionMatrix, viewProjectionMatrix, rotateY, [0, 1, 0]);
            } else if (item.indexOf('rotateZ') !== -1) {
                rotateZ = -parseFloat(item.replace('rotateZ(', '').replace('deg)', '')) * (Math.PI / 180);
                mat4.rotate(viewProjectionMatrix, viewProjectionMatrix, rotateZ, [0, 0, 1]);
            } else if (item.indexOf('translateX') !== -1) {
                translateX = __convertToValue(item.replace('translateX(', ''), true) * aspectRatio;
                mat4.translate(viewProjectionMatrix, viewProjectionMatrix, [translateX / (canvas.width / 2), 0, 0]);
            } else if (item.indexOf('translateY') !== -1) {
                translateY = -__convertToValue(item.replace('translateY(', ''), false);
                mat4.translate(viewProjectionMatrix, viewProjectionMatrix, [0, translateY / (canvas.height / 2), 0]);
            } else if (item.indexOf('translateZ') !== -1) {
                translateZ = parseFloat(item.replace('translateZ(', '').replace('px)', ''));
                mat4.translate(viewProjectionMatrix, viewProjectionMatrix, [0, 0, (translateZ / perspective) * f]);
            }
        });

        mat4.scale(viewProjectionMatrix, viewProjectionMatrix, [aspectRatio, 1, 1]);
        this._gl.uniformMatrix4fv(u_matrix, false, viewProjectionMatrix);
    }

    _initProgram(size) {
        this._gl = this._canvas.getContext('webgl');
        this._program = initProgram(this._gl, VERTEX_SHADER_SOURCE, FRAGMENT_SHADER_SOURCE);
        if (!this._program) {
            return;
        }
        initVertexBuffer(this._gl);

        // When re-initializing the program, clean up old texture resources and state
        if (this._textureObject) {
            this._gl.deleteTexture(this._textureObject);
            this._textureObject = null;
        }
        this._lastCanvasWidth = 0;
        this._lastCanvasHeight = 0;
    }
}
export { FJCssPerspectiveToWebGLStore };
const fjCssPerspectiveToWebGLStore = new FJCssPerspectiveToWebGLStore();
export default fjCssPerspectiveToWebGLStore;
